// DBT Skills — full interactive content library.
// Content is trauma-informed, plain English, UK spelling. Educational only.

export type Flashcard = { front: string; back: string };

export type ScenarioOption = {
  text: string;
  correct: boolean;
  feedback: string;
};

export type ScenarioQuiz = {
  prompt: string;
  options: ScenarioOption[];
};

export interface DbtSkill {
  id: string;
  title: string;
  emoji: string;
  /** One-line summary shown in module lists. */
  summary: string;
  /** 2–4 short paragraphs — the 5-minute read. */
  learn: string[];
  flashcards: Flashcard[];
  practicePrompt: string;
  scenario: ScenarioQuiz;
  reflectionPrompt: string;
}

export type DbtModuleId =
  | "mindfulness"
  | "distress-tolerance"
  | "emotion-regulation"
  | "interpersonal"
  | "middle-path";

export interface DbtModule {
  id: DbtModuleId;
  label: string;
  emoji: string;
  tagline: string;
  intro: string;
  /** LearnCarousel theme key, e.g. "sage" | "sky" | "peach" | "lavender" | "gold". */
  theme: "sage" | "sky" | "peach" | "lavender" | "gold";
  skills: DbtSkill[];
}

export const DBT_INTRO = `Dialectical Behaviour Therapy (DBT) was developed by Dr Marsha Linehan and is one of the most effective approaches for people who feel emotions very intensely. Across five modules, DBT teaches practical skills for the moments when life feels too big — mindfulness, distress tolerance, emotion regulation, interpersonal effectiveness, and walking the middle path.`;

// ─────────────────────────────────────────────────────────────
// 1. MINDFULNESS
// ─────────────────────────────────────────────────────────────
const mindfulness: DbtSkill[] = [
  {
    id: "wise-mind",
    title: "Wise Mind",
    emoji: "🧠",
    summary: "The calm place where logic and emotion meet.",
    learn: [
      "Wise Mind is the meeting point between your reasonable mind (facts, logic, planning) and your emotion mind (feelings, urges, intuition). Neither alone gives the full picture — Wise Mind holds both.",
      "It doesn't override your feelings and it doesn't ignore reality. It's the steady inner knowing that says, 'this matters, and here is what will actually help.' Most people can feel it as a settling in the chest or a quiet certainty.",
      "You reach Wise Mind by slowing down. Breathe. Drop attention into your body. Ask a question and wait — don't chase the answer. The response that arrives from a place of calm, rather than panic or shutdown, is usually Wise Mind speaking.",
    ],
    flashcards: [
      { front: "🧠 Reasonable Mind", back: "Facts, logic, planning. Useful — but cold on its own." },
      { front: "❤️ Emotion Mind", back: "Feelings, urges, intuition. Powerful — but can drive impulsive moves." },
      { front: "🕊️ Wise Mind", back: "Both together. Calm knowing. The response that helps you long-term." },
      { front: "How to access it", back: "Breathe. Drop into the body. Ask a question. Wait for the answer that feels settled." },
    ],
    practicePrompt: "Bring to mind one decision you're wrestling with right now. Ask Wise Mind: what do I actually know here?",
    scenario: {
      prompt: "You've had a hard day. You're standing at the fridge deciding whether to open a bottle of wine. What's the Wise Mind move?",
      options: [
        { text: "Pour a large glass — you deserve it.", correct: false, feedback: "That's emotion mind driving. It might feel true — but Wise Mind holds the whole picture, including tomorrow morning." },
        { text: "Lecture yourself about how bad alcohol is.", correct: false, feedback: "That's reasonable mind on its own, and it usually backfires. Wise Mind isn't cold." },
        { text: "Pause. Breathe. Ask: what do I actually need right now?", correct: true, feedback: "Yes. Wise Mind starts with the pause. The honest answer might be a bath, a call, or the wine — but chosen, not reached for." },
        { text: "Ignore the feeling and go to bed.", correct: false, feedback: "Suppressing emotion mind doesn't get you to Wise Mind — it just delays it." },
      ],
    },
    reflectionPrompt: "When was the last time you felt Wise Mind — that quiet, settled knowing? What was happening in your body?",
  },
  {
    id: "observe",
    title: "Observe",
    emoji: "👁️",
    summary: "Notice what's here without adding a story.",
    learn: [
      "Observing is paying attention to what's actually happening — inside you and around you — without labelling, judging, or trying to change it. It's the first mindfulness skill because everything else builds on it.",
      "Most of us jump straight to interpretation. 'She looked at me like that — she must be angry.' Observing pulls you back a step: what did you actually see? A face. A tone. A feeling in your chest.",
      "Practise with something small. Look at your hand. Feel the chair under you. Notice a sound. Don't describe it, don't judge it — just let it register. This muscle strengthens quickly with use.",
    ],
    flashcards: [
      { front: "👁️ What is Observe?", back: "Paying attention to what's actually here — no labels, no story." },
      { front: "Inside", back: "Sensations, emotions, thoughts moving through." },
      { front: "Outside", back: "Sights, sounds, smells, textures around you." },
      { front: "Common trap", back: "Slipping into interpretation. 'She's angry' is a story — 'her voice got sharp' is what you observed." },
    ],
    practicePrompt: "Spend 60 seconds observing. Name three things you notice — inside your body and outside — without adding a story.",
    scenario: {
      prompt: "Your partner walks in and doesn't say hello. You feel your chest tighten. What's Observe here?",
      options: [
        { text: "'They're annoyed with me.'", correct: false, feedback: "That's interpretation, not observation. It might be true — but it's a story your mind added." },
        { text: "'They didn't say hello. My chest is tight.'", correct: true, feedback: "Exactly. Facts inside and outside, no story yet. From here you can respond, not react." },
        { text: "'I always do something wrong.'", correct: false, feedback: "That's judgement piled on top of interpretation. Observe pulls you back before that spiral starts." },
        { text: "'I should ask what's wrong.'", correct: false, feedback: "That might be a good next step — but it's not observing. Notice first, act second." },
      ],
    },
    reflectionPrompt: "What did you observe today that you would normally have skipped straight past?",
  },
  {
    id: "describe",
    title: "Describe",
    emoji: "💬",
    summary: "Put words on what you notice — facts, not judgements.",
    learn: [
      "Describe is the second step after Observe. You take what you noticed and put it into words — sticking to what's actually there, not what you assume it means.",
      "The rule: only describe what you could point to. 'My jaw is tight' is describable. 'I'm being pathetic' is not — it's a judgement dressed up as a fact.",
      "Describing pulls thoughts out of the fog and makes them workable. Once something is named clearly, you can choose what to do with it. Left un-named, it runs the show.",
    ],
    flashcards: [
      { front: "💬 Rule of Describe", back: "Only what you could point to. Facts, not stories." },
      { front: "Good example", back: "'My chest is warm. My hands are shaking. I'm thinking about tomorrow.'" },
      { front: "Trap", back: "'I'm being ridiculous.' That's a judgement, not a description." },
      { front: "Why it helps", back: "Naming shrinks the fog. What you can describe, you can work with." },
    ],
    practicePrompt: "Describe what's happening in your body right now in three sentences. Facts only — no judgements.",
    scenario: {
      prompt: "You're about to send a difficult message. Which of these is a description, not a judgement?",
      options: [
        { text: "'I'm such a coward for hesitating.'", correct: false, feedback: "Judgement. Describe would be: 'My finger is hovering over Send. My stomach is fluttery.'" },
        { text: "'This is stupid, I should just do it.'", correct: false, feedback: "Judgement again. Observe first — what's actually here?" },
        { text: "'My stomach is tight. I've re-read the message four times. There's a thought that says they won't reply.'", correct: true, feedback: "Yes — pure description. No judgement. From here you can make a real choice." },
        { text: "'I always mess these up.'", correct: false, feedback: "That's a story about the past, not a description of now." },
      ],
    },
    reflectionPrompt: "What's one judgement you often use about yourself? What would that same moment sound like if you only described it?",
  },
  {
    id: "participate",
    title: "Participate",
    emoji: "🎭",
    summary: "Enter the current moment fully — one thing at a time.",
    learn: [
      "Participate is throwing yourself into what you're doing, with your whole self, without the running commentary. Washing dishes as if that's the only thing happening in the world. Dancing without watching yourself dance.",
      "It's the antidote to going through life at arm's length — half-present, half-watching yourself, half-somewhere-else. Trauma often teaches us to stay slightly outside our own experience. Participating gently invites you back in.",
      "Start with something safe and small. A song you love. A shower. A meal. Let yourself be inside it for the length of that one thing — not performing it, not evaluating it. Just there.",
    ],
    flashcards: [
      { front: "🎭 What is Participate?", back: "Throwing yourself fully into the current moment — one thing at a time." },
      { front: "Where to start", back: "Something small and safe. A song. A shower. A meal you love." },
      { front: "Common trap", back: "Watching yourself instead of being in it. Notice, then drop back in." },
      { front: "Why it heals", back: "Trauma teaches distance. Participating gently invites you back into your life." },
    ],
    practicePrompt: "Pick one activity today — even brushing your teeth — and be fully in it. Notice when your mind pulls you out, and return.",
    scenario: {
      prompt: "You're on a walk with a friend, but your mind is on tomorrow's meeting. What's participating?",
      options: [
        { text: "Force yourself not to think about tomorrow.", correct: false, feedback: "Fighting thoughts pulls you further out. Participating is gentler — notice, and return." },
        { text: "Notice you drifted, feel your feet on the ground, come back to what your friend is saying.", correct: true, feedback: "Yes. Participation isn't perfect focus — it's the returning that matters." },
        { text: "Give up and let your mind plan the meeting.", correct: false, feedback: "That's checking out, not participating." },
        { text: "Tell your friend you can't focus.", correct: false, feedback: "Sometimes honest — but if you can still choose to be here, do that first." },
      ],
    },
    reflectionPrompt: "When did you last feel fully inside an experience — not watching, not evaluating? What made it possible?",
  },
];

// ─────────────────────────────────────────────────────────────
// 2. DISTRESS TOLERANCE
// ─────────────────────────────────────────────────────────────
const distressTolerance: DbtSkill[] = [
  {
    id: "tipp",
    title: "TIPP Skills",
    emoji: "❄️",
    summary: "Fast body-based ways to calm a nervous system in crisis.",
    learn: [
      "TIPP is DBT's emergency toolkit for when emotions are so intense you can't think straight. It works by using your body to change your body's state — no talking your way out of it required.",
      "The four skills are Temperature, Intense exercise, Paced breathing, and Paired muscle relaxation. Each one shifts your physiology directly, which is why they work even when nothing else will.",
      "Use TIPP when the intensity is a 7 out of 10 or higher — panic, rage, overwhelm, urge to self-harm. It doesn't fix the problem; it brings the volume down enough for you to think again.",
    ],
    flashcards: [
      { front: "❄️ T — Temperature", back: "Cold water on your face (30 seconds), or hold ice. Triggers the dive reflex — instant nervous-system calm." },
      { front: "🏃 I — Intense Exercise", back: "30–60 seconds of full-out movement: jumping jacks, sprint on the spot, stairs. Burns off stress hormones." },
      { front: "🌬️ P — Paced Breathing", back: "In for 4. Out for 6–8. Longer exhale than inhale. Calms your vagus nerve." },
      { front: "💪 P — Paired Muscle Relaxation", back: "Tense a muscle group for 5 seconds. Release. Feel the difference. Move through the body." },
    ],
    practicePrompt: "Try one TIPP skill right now — even in a calm moment. What do you notice in your body afterwards?",
    scenario: {
      prompt: "You're mid-panic attack. Your chest is tight, your thoughts are racing. Which TIPP move is likely to help fastest?",
      options: [
        { text: "Journal about why you're anxious.", correct: false, feedback: "Not TIPP — and probably too much thinking for this level of activation." },
        { text: "Splash cold water on your face for 30 seconds.", correct: true, feedback: "Yes. Temperature is the fastest — it triggers the dive reflex and drops heart rate within seconds." },
        { text: "Try to breathe deeply while lying down.", correct: false, feedback: "Better than nothing, but at 9/10 activation, temperature acts faster than breathing alone." },
        { text: "Call someone and explain the panic.", correct: false, feedback: "Connection matters — but bring the nervous system down first, then reach out." },
      ],
    },
    reflectionPrompt: "Which TIPP skill do you already use without realising? Which one do you want to have ready next time?",
  },
  {
    id: "stop",
    title: "STOP Skill",
    emoji: "🛑",
    summary: "The four-second pause between urge and action.",
    learn: [
      "STOP is the tiny gap you create between a strong urge and doing the thing you'd regret. It stands for Stop, Take a step back, Observe, Proceed mindfully.",
      "It's not about suppressing the urge — it's about not letting the urge drive without your consent. Four seconds is often enough to change the outcome.",
      "STOP works best when you rehearse it before you need it. Practise on small moments — the urge to snap at a slow driver, to send the reply while angry. Build the muscle when the stakes are low.",
    ],
    flashcards: [
      { front: "🛑 S — Stop", back: "Freeze. Don't move, don't speak, don't send." },
      { front: "👣 T — Take a step back", back: "Breathe. Physically move if you can. Get a little distance from the trigger." },
      { front: "👀 O — Observe", back: "Notice: thoughts, emotions, body sensations, surroundings. What's actually here?" },
      { front: "✅ P — Proceed mindfully", back: "Choose the response that helps your long-term goals — not the impulsive one." },
    ],
    practicePrompt: "Think of a small urge you often act on without thinking (checking your phone, snapping a reply). Rehearse STOP for that moment.",
    scenario: {
      prompt: "A colleague criticises you in a meeting. Your face is hot. What's STOP look like?",
      options: [
        { text: "Argue back to defend yourself.", correct: false, feedback: "That's the urge driving. STOP creates the pause before that happens." },
        { text: "Freeze, breathe, notice the heat in your face, then choose your reply.", correct: true, feedback: "Yes. All four steps in a few seconds — enough to respond as who you want to be." },
        { text: "Say nothing and stew about it for a week.", correct: false, feedback: "That's shutdown, not STOP. STOP ends with a chosen response, not silence by default." },
        { text: "Leave the meeting entirely.", correct: false, feedback: "Sometimes right — but only after the Observe step. Don't let the leaving be the impulse." },
      ],
    },
    reflectionPrompt: "What's one situation where a four-second STOP would have changed the day?",
  },
  {
    id: "accepts",
    title: "ACCEPTS",
    emoji: "🎨",
    summary: "Seven ways to distract wisely when you can't fix it yet.",
    learn: [
      "Sometimes an emotion is too big to sit with and too big to solve. ACCEPTS gives you seven doorways out of the intensity — long enough for the wave to pass.",
      "This isn't avoidance. Avoidance is running from something you'll never face. Distraction is stepping aside while the storm goes over, so you can come back and deal with it from calmer ground.",
      "Pick one deliberately. The choice matters — 'I'm going to bake something' works better than scrolling for two hours and hating yourself for it.",
    ],
    flashcards: [
      { front: "🎨 A — Activities", back: "Do something absorbing: cook, clean, walk, garden, puzzle." },
      { front: "🤝 C — Contributing", back: "Help someone. Volunteer. Text a friend who's struggling." },
      { front: "⚖️ C — Comparisons", back: "Remember a harder time you got through. Not to minimise now — to remind yourself you can." },
      { front: "😊 E — Emotions", back: "Deliberately generate a different feeling: sad movie, funny podcast, upbeat music." },
      { front: "🚪 P — Push away", back: "Mentally box the problem up. 'I'll come back to this at 3pm.' Then actually do." },
      { front: "🧩 T — Thoughts", back: "Occupy your mind: count backwards from 100 by 7s, recite lyrics, do a puzzle." },
      { front: "🌊 S — Sensations", back: "Strong safe sensation: cold shower, spicy food, a hot bath, a scented candle." },
    ],
    practicePrompt: "Pick two ACCEPTS options you'd genuinely use. Write them somewhere visible for the next hard moment.",
    scenario: {
      prompt: "It's 11pm. You're spiralling about something a friend said. You can't fix it tonight. What's an ACCEPTS move?",
      options: [
        { text: "Draft the confrontation text and re-read it twenty times.", correct: false, feedback: "That's the spiral. ACCEPTS is stepping aside from it, not feeding it." },
        { text: "Put on a familiar comfort film and let yourself be absorbed in it (Activities + Emotions).", correct: true, feedback: "Yes. Deliberate, chosen, and it moves you out of the spiral so tomorrow you can respond clearly." },
        { text: "Scroll social media until you fall asleep.", correct: false, feedback: "Passive scrolling usually makes things worse. Choose the distraction — don't drift into it." },
        { text: "Force yourself to sleep.", correct: false, feedback: "Rarely works mid-spiral. Get the nervous system down first — then sleep comes." },
      ],
    },
    reflectionPrompt: "Which ACCEPTS options genuinely help you? Which ones look good on paper but leave you worse off?",
  },
  {
    id: "self-soothe",
    title: "Self-Soothe with Five Senses",
    emoji: "🕯️",
    summary: "Kindness through sight, sound, smell, taste, touch.",
    learn: [
      "Self-soothing is treating yourself the way you'd treat someone you love who was hurting. It uses the five senses because they reach a part of the nervous system words can't.",
      "It's not indulgence and it's not a reward you have to earn. It's basic nervous-system care — the same way a warm bath or a favourite song will steady almost anyone.",
      "Build a small self-soothing kit for hard moments: one thing for each sense. Keep it somewhere you'll actually reach for it.",
    ],
    flashcards: [
      { front: "👁️ Sight", back: "Something beautiful — a plant, art, sunset, a photo you love." },
      { front: "👂 Sound", back: "A favourite song. Rain. A specific playlist for hard moments." },
      { front: "👃 Smell", back: "A candle, lotion, coffee, fresh herbs. Scent hits fastest." },
      { front: "👅 Taste", back: "Tea, dark chocolate, fruit. Something slow, savoured." },
      { front: "✋ Touch", back: "Soft blanket, warm bath, weighted item, hand on chest." },
    ],
    practicePrompt: "Pick one thing for each sense that soothes you. Put them somewhere you can reach when you need them.",
    scenario: {
      prompt: "You've had a brutal call. You have twenty minutes before the next thing. What's a self-soothe?",
      options: [
        { text: "Push through — no time for this.", correct: false, feedback: "Twenty minutes is enough. Not soothing now means you'll bring the brutal call into the next thing." },
        { text: "Make a proper cup of tea. Sit somewhere warm. Something scented if you have it.", correct: true, feedback: "Yes. Small, sensory, chosen — that's self-soothe. Twenty minutes well spent." },
        { text: "Scroll to distract yourself.", correct: false, feedback: "Distraction, not soothing. Both have their place — but sensory care resets you more deeply." },
        { text: "Vent at the next person you see.", correct: false, feedback: "That's discharge, and it usually costs the relationship. Soothe the body first." },
      ],
    },
    reflectionPrompt: "Where did you learn what kindness feels like? What's one act of self-soothing that felt genuinely tender this week?",
  },
  {
    id: "improve",
    title: "IMPROVE the Moment",
    emoji: "🌤️",
    summary: "Seven ways to make a hard moment slightly more bearable.",
    learn: [
      "IMPROVE is for the moments you can't fix or leave — the wait, the appointment, the flight, the difficult night. It's not about pretending things are fine. It's about making them a fraction more livable.",
      "The seven ingredients: Imagery, Meaning, Prayer, Relaxation, One thing at a time, Vacation (a short mental break), Encouragement.",
      "Pick one that feels honest right now. Even five minutes counts. The point is not transformation — it's the small shift that keeps you here until things change.",
    ],
    flashcards: [
      { front: "🖼️ I — Imagery", back: "Picture a safe place in detail. Colours, sounds, textures." },
      { front: "🎯 M — Meaning", back: "Ask: what could this moment teach me? What matters to me here?" },
      { front: "🙏 P — Prayer", back: "Prayer, or asking Wise Mind, or the universe. Any honest reaching-out." },
      { front: "🌊 R — Relaxation", back: "Slow breathing, warm bath, muscle release." },
      { front: "1️⃣ O — One thing at a time", back: "Drop the future. Just this next thing. Then the next." },
      { front: "🏝️ V — Vacation", back: "A short deliberate mental break: five minutes with a magazine, a nap, a walk." },
      { front: "💛 E — Encouragement", back: "Talk to yourself like a coach: 'I've done hard things before. I can do this next hour.'" },
    ],
    practicePrompt: "Which IMPROVE ingredients feel most honest for you? Choose two to try in the next hard moment.",
    scenario: {
      prompt: "You're in a hospital waiting room. Two hours to go. Which IMPROVE move helps?",
      options: [
        { text: "Doom-scroll and check the time every three minutes.", correct: false, feedback: "That's the opposite — it stretches the two hours into forever." },
        { text: "One thing at a time: notice the room, breathe slowly, encourage yourself gently.", correct: true, feedback: "Yes. Present-moment focus + kindness. Two hours becomes 120 individual minutes you can meet." },
        { text: "Get angry that it's taking so long.", correct: false, feedback: "Feelings are valid — but IMPROVE is what you do with them. Anger without a channel stretches the wait." },
        { text: "Leave and reschedule.", correct: false, feedback: "That's exiting, not IMPROVE. IMPROVE is for moments you can't leave." },
      ],
    },
    reflectionPrompt: "What's one hard moment coming up this week? Which IMPROVE ingredient will you have ready?",
  },
  {
    id: "pros-cons",
    title: "Pros & Cons",
    emoji: "⚖️",
    summary: "Weigh acting on the urge vs. riding it out.",
    learn: [
      "When an urge is strong, your brain shows you a shiny short-term picture — relief, escape, revenge. Pros and Cons puts the other side on paper, so both are visible before you choose.",
      "The trick is to write four boxes, not two: pros of acting on the urge, cons of acting, pros of NOT acting, cons of not acting. All four honest.",
      "Read them back to yourself out loud if you can. Something changes when the full picture is in front of you — the urge stops running the show without your consent.",
    ],
    flashcards: [
      { front: "⚖️ Four boxes", back: "Pros of acting · Cons of acting · Pros of NOT acting · Cons of NOT acting." },
      { front: "Rule", back: "Be honest in all four. Include what the urge is really offering." },
      { front: "When to use", back: "Any strong urge you sense you'd regret — spending, contact, substance, self-harm." },
      { front: "Why it works", back: "Makes long-term consequences visible when short-term reward is loudest." },
    ],
    practicePrompt: "Pick one urge you've been wrestling with. Draw four boxes. Fill them all honestly.",
    scenario: {
      prompt: "You want to text your ex at 2am. What's the most useful Pros & Cons approach?",
      options: [
        { text: "Skip the exercise — you already know what's right.", correct: false, feedback: "If you already know, why is the finger over the button? Writing it out is what makes 'knowing' land." },
        { text: "Only list cons of texting, to talk yourself out of it.", correct: false, feedback: "That's suppression, and the urge usually returns stronger. Include all four honestly." },
        { text: "Write all four boxes honestly — including what texting would give you and what not texting costs.", correct: true, feedback: "Yes. Honesty is what makes it work. The full picture almost always changes the choice." },
        { text: "Text now and Pros & Cons after.", correct: false, feedback: "That's the urge writing your reasoning. Pros & Cons is meant for the pause, not the debrief." },
      ],
    },
    reflectionPrompt: "What's one urge Pros & Cons has changed your mind about — or could next time?",
  },
  {
    id: "radical-acceptance",
    title: "Radical Acceptance",
    emoji: "🌄",
    summary: "Fully accepting reality — so you can respond to what's here.",
    learn: [
      "Radical Acceptance is fully acknowledging reality as it is — the diagnosis, the loss, the fact that this happened to you. Not liking it. Not approving of it. Just stopping the war with the fact that it's true.",
      "It's the opposite of denial and the opposite of forcing yourself to 'be fine.' It's a full-body recognition: this is what is. Even if I hate it. Even if it wasn't fair.",
      "Radical Acceptance doesn't heal the wound — but it stops you spending your energy fighting reality, which frees you to actually respond to what's in front of you.",
    ],
    flashcards: [
      { front: "🌄 What it is", back: "Fully acknowledging reality — including the parts you didn't choose and don't like." },
      { front: "What it isn't", back: "Approval. Forgiveness. Giving up. Pretending it's fine." },
      { front: "The core sentence", back: "'This is what is. Fighting it costs me more than accepting it.'" },
      { front: "Why it helps", back: "You stop bleeding energy into 'it shouldn't be this way' — and can respond to what's here." },
    ],
    practicePrompt: "What's one thing you've been fighting the reality of? Write one sentence beginning: 'This is what is…'",
    scenario: {
      prompt: "Your mum has been diagnosed with something serious. You keep replaying 'this shouldn't be happening.' What's radical acceptance?",
      options: [
        { text: "Force yourself to be positive about it.", correct: false, feedback: "That's suppression, not acceptance. Acceptance doesn't ask you to feel good about it." },
        { text: "Deny it — refuse to think about it until the appointment.", correct: false, feedback: "Denial keeps the fight going underground. Acceptance is looking at it directly." },
        { text: "Sit with the sentence: 'This is what is. I hate it. And it's true.' Then decide what today needs.", correct: true, feedback: "Yes. Full acknowledgement — including the hatred of it — is what frees you to actually be present with your mum." },
        { text: "Accept it and move on quickly.", correct: false, feedback: "Acceptance isn't the same as moving on. It's the ground you stand on while grief does its work." },
      ],
    },
    reflectionPrompt: "What are you at war with — some fact of your life or history — that Radical Acceptance would gently loosen?",
  },
  {
    id: "turning-the-mind",
    title: "Turning the Mind",
    emoji: "🔁",
    summary: "Choosing acceptance again, and again, when it slips.",
    learn: [
      "Radical Acceptance isn't a one-time event. Most people accept a hard truth in the morning and are fighting it again by evening. Turning the Mind is the practice of choosing acceptance again — as many times as it takes.",
      "Each time you notice the fight is back ('this shouldn't be happening'), you turn your mind, gently, back towards acceptance. Not with force. Not with shame. Just: 'oh — I've slipped. This is what is. Again.'",
      "Some things need turning the mind ten times a day for months. That's not failing — that's how deep acceptance actually gets built.",
    ],
    flashcards: [
      { front: "🔁 What it is", back: "Choosing acceptance again — every time your mind slips back into fighting reality." },
      { front: "The trigger", back: "Noticing thoughts like 'this shouldn't be happening', 'why me', 'it's not fair.'" },
      { front: "The move", back: "Gently: 'I've slipped. This is what is. Again.' No shame — just the turn." },
      { front: "Frequency", back: "Ten times a day is normal for hard things. Repetition IS the practice." },
    ],
    practicePrompt: "Notice today when you slip back into fighting a reality you'd already accepted. How many times? What does the turn feel like?",
    scenario: {
      prompt: "You accepted the breakup yesterday. Today you keep spiralling into 'this shouldn't be happening.' What's turning the mind?",
      options: [
        { text: "Get angry at yourself for not being over it.", correct: false, feedback: "That's shame, not turning the mind. Turning is gentle — 'oh, I've slipped' — and then back." },
        { text: "Notice the fight. Say 'this is what is' again. Return to what today needs.", correct: true, feedback: "Yes. The slip isn't the failure — the turn is the practice. As many times as needed." },
        { text: "Force yourself to think positive thoughts about being single.", correct: false, feedback: "Forcing positivity is another way of fighting reality. Acceptance goes underneath it." },
        { text: "Decide acceptance doesn't work for you.", correct: false, feedback: "The slip is proof it's working — you noticed. That's the whole skill." },
      ],
    },
    reflectionPrompt: "What have you accepted, then had to accept again? What's it like to know that's normal — not a failure?",
  },
];

// ─────────────────────────────────────────────────────────────
// 3. EMOTION REGULATION
// ─────────────────────────────────────────────────────────────
const emotionRegulation: DbtSkill[] = [
  {
    id: "understanding-emotions",
    title: "Understanding Emotions",
    emoji: "🌈",
    summary: "Emotions are messengers — not enemies.",
    learn: [
      "Every emotion has a job. Fear tells you something is dangerous. Anger tells you a boundary was crossed. Sadness tells you something mattered. Even the ones you were taught to hide are trying to help.",
      "An emotion has three parts: a body sensation, a thought, and an urge. Fear = tight chest + 'something's wrong' + urge to hide. Learning to notice all three lets you meet the emotion instead of being run by it.",
      "You don't have to obey an emotion to honour it. Anger's message might be 'a boundary was crossed' — but the wise response often isn't shouting. Listen to the message. Choose the response.",
    ],
    flashcards: [
      { front: "🌈 Every emotion has a job", back: "Fear = danger. Anger = boundary. Sadness = loss. Joy = worth repeating. Shame = fit with the group." },
      { front: "Three parts", back: "Body sensation + thought + urge. Notice all three." },
      { front: "Message vs. urge", back: "Honour the message. Choose the response. Anger's message ≠ shouting." },
      { front: "Emotions ≠ enemies", back: "They're old, fast, and imperfect — but they're on your side." },
    ],
    practicePrompt: "Bring one emotion from today to mind. Name the body sensation, the thought, and the urge. What's it trying to tell you?",
    scenario: {
      prompt: "You feel a wave of anger when a friend cancels last-minute. What's the wise response?",
      options: [
        { text: "Suppress it — it's not a big deal.", correct: false, feedback: "Suppressing skips the message. The anger might be telling you something matters here." },
        { text: "Send a sharp text and delete the friendship.", correct: false, feedback: "That's the urge driving. Message honoured, response not chosen." },
        { text: "Notice the anger. Ask what it's telling you. Then decide how to respond honestly.", correct: true, feedback: "Yes. The anger might be 'I feel unimportant to them' — worth talking about, not exploding about." },
        { text: "Assume you're being unreasonable and shut it down.", correct: false, feedback: "That's dismissing your own inner data. Listen first — then evaluate." },
      ],
    },
    reflectionPrompt: "Which emotion were you taught to hide? What might it have been trying to tell you all along?",
  },
  {
    id: "emotion-wheel",
    title: "Emotion Wheel",
    emoji: "🎡",
    summary: "Precise names change everything — 'sad' isn't specific enough.",
    learn: [
      "Most people describe emotions in about four words: fine, stressed, sad, angry. The emotion wheel offers you dozens of more precise names — because 'lonely' asks for something different from 'rejected,' even though both live under 'sad.'",
      "Naming precisely does something in the brain — it shifts activity from the amygdala (alarm) to the prefrontal cortex (thinking). You literally regulate as you name.",
      "You don't need to memorise the wheel. Just keep asking: what's the more precise word? 'Angry' → 'resentful.' 'Sad' → 'grieving.' Each layer of precision brings more choice.",
    ],
    flashcards: [
      { front: "🎡 Why precise names help", back: "Naming shifts brain activity from alarm to thinking. You regulate as you name." },
      { front: "Under 'sad'", back: "Lonely, grieving, disappointed, hurt, rejected, ashamed, hopeless." },
      { front: "Under 'angry'", back: "Frustrated, resentful, betrayed, dismissed, jealous, humiliated." },
      { front: "The practice", back: "Ask: what's the more precise word? Keep going until it clicks." },
    ],
    practicePrompt: "Pick one emotion from today. Find three more precise words for it. Which one lands the deepest?",
    scenario: {
      prompt: "You keep saying you're 'stressed' about work. What's a more useful next move?",
      options: [
        { text: "Ignore the label — it doesn't matter what word you use.", correct: false, feedback: "It matters. 'Stressed' is too vague to act on. 'Resentful' or 'overwhelmed' point to different responses." },
        { text: "Ask: is it overwhelmed, resentful, scared, or bored? Then work with the actual one.", correct: true, feedback: "Yes. Each of those needs something different. Precision is the doorway to the right action." },
        { text: "Take a stress-management course.", correct: false, feedback: "Might help — but not until you know what 'stress' actually is for you." },
        { text: "Push through and hope it passes.", correct: false, feedback: "Same emotion, still unnamed, will keep returning." },
      ],
    },
    reflectionPrompt: "Which word do you overuse for your feelings? What might sit underneath it?",
  },
  {
    id: "check-the-facts",
    title: "Check the Facts",
    emoji: "🔍",
    summary: "Does your emotion match what actually happened?",
    learn: [
      "Emotions are fast, and they often fit the story you're telling yourself more than the actual facts. Check the Facts is the pause where you compare the two.",
      "Walk through: what actually happened? What am I assuming? What's the interpretation my emotion is running with? Is there another interpretation that also fits?",
      "You're not disproving the emotion. You're asking whether its size and story fit the real situation. Often it does — and you act. Sometimes it doesn't — and you get to soften without dismissing yourself.",
    ],
    flashcards: [
      { front: "🔍 What to check", back: "Facts vs. the story your emotion is running with." },
      { front: "The questions", back: "What actually happened? What am I assuming? Is another interpretation possible?" },
      { front: "Not the goal", back: "You're not disproving the emotion. You're checking whether its size fits the facts." },
      { front: "If it fits", back: "Trust it. Act." },
      { front: "If it doesn't", back: "Soften — without dismissing yourself. Then choose the fitted response." },
    ],
    practicePrompt: "Bring one strong emotion from this week to mind. What facts do you know? What are you assuming? What else might be true?",
    scenario: {
      prompt: "Your boss walked past without saying hello. You feel a wave of 'I'm about to be fired.' What are the facts?",
      options: [
        { text: "Facts: my boss hates me. Emotion fits.", correct: false, feedback: "'Hates me' is an interpretation, not a fact. What did you actually observe?" },
        { text: "Facts: my boss walked past without speaking. Interpretations available: distracted, in a rush, on a call, upset. Fear about being fired doesn't fit these facts.", correct: true, feedback: "Yes. Emotion noticed, story checked, softened without being dismissed." },
        { text: "Facts don't matter — the feeling is real.", correct: false, feedback: "Feelings are real AND stories can be inaccurate. Both are true at once." },
        { text: "Update your CV immediately.", correct: false, feedback: "That's acting on the unchecked story. Check first — then decide." },
      ],
    },
    reflectionPrompt: "What's a story your emotions often tell you that, when you check, doesn't quite fit the facts?",
  },
  {
    id: "opposite-action",
    title: "Opposite Action",
    emoji: "🔄",
    summary: "When the emotion doesn't fit — act opposite, all in.",
    learn: [
      "Every emotion comes with an urge: fear wants you to hide, anger wants you to attack, shame wants you to disappear. When the emotion fits the facts, follow the urge. When it doesn't, act the opposite — with your whole self.",
      "The 'with your whole self' part matters. If shame is telling you to hide and you decide it doesn't fit, you don't half-connect while frowning. You warmly connect, body language and all. Half-hearted opposite action doesn't shift the emotion.",
      "Opposite action rewires. Each time you do the opposite of an emotion that doesn't fit, the emotion loses grip. Over months, the pattern changes.",
    ],
    flashcards: [
      { front: "🔄 When to use it", back: "When the emotion doesn't fit the facts (checked with Check the Facts)." },
      { front: "Fear → Approach", back: "Instead of hiding, approach in small steps. All in." },
      { front: "Anger → Gentle avoid", back: "Instead of attacking, gently distance until the urge passes." },
      { front: "Shame → Show up", back: "Instead of hiding, connect warmly — body language and all." },
      { front: "Sadness → Activate", back: "Instead of withdrawing, move, engage, do the small thing anyway." },
    ],
    practicePrompt: "What emotion has you in a pattern you'd like to change? What would the opposite action look like — all in?",
    scenario: {
      prompt: "You're ashamed about something small and want to cancel dinner with a friend. You've checked — the shame doesn't fit the facts. What's opposite action?",
      options: [
        { text: "Cancel dinner and journal about the shame.", correct: false, feedback: "That's following the urge. Opposite action is going — and going with your whole self." },
        { text: "Go, but keep your head down and leave early.", correct: false, feedback: "That's half-hearted opposite action. Shame doesn't shift unless you show up fully." },
        { text: "Go. Sit warmly. Make eye contact. Let yourself be seen.", correct: true, feedback: "Yes. All-in opposite action is what rewires the pattern." },
        { text: "Force yourself to feel confident before you go.", correct: false, feedback: "You don't need to feel it first. Act opposite, and the feeling follows over time." },
      ],
    },
    reflectionPrompt: "What emotion drives you into a pattern you'd like to change? What's one all-in opposite action you could try?",
  },
  {
    id: "abc-please",
    title: "ABC PLEASE",
    emoji: "🌸",
    summary: "Daily habits that protect your emotional bandwidth.",
    learn: [
      "ABC PLEASE is DBT's foundation for keeping your emotional threshold high — because a tired, hungry, dysregulated body can't skill its way out of anything.",
      "ABC is Accumulate positives, Build mastery, Cope ahead — deliberately weaving in good experiences, small competence, and prepared plans. PLEASE is basic body care: treat PhysicaL illness, balanced Eating, avoid mood-Altering substances, balanced Sleep, get Exercise.",
      "None of this is glamorous. All of it makes the other skills possible. When someone's emotions are constantly overwhelming, PLEASE is often what's missing.",
    ],
    flashcards: [
      { front: "🌸 A — Accumulate positives", back: "Small good things daily. Bigger meaningful things weekly. Values-aligned life monthly." },
      { front: "💪 B — Build mastery", back: "One small competence a day. Something slightly hard, done." },
      { front: "🎯 C — Cope ahead", back: "Rehearse the hard thing before it happens. Plan the skills." },
      { front: "🏥 PL — Physical iLlness", back: "Treat what's medical. Untreated pain and illness dysregulate everything." },
      { front: "🍎 E — Eating", back: "Balanced, regular. Blood sugar affects mood more than most people know." },
      { front: "🚫 A — Avoid mood-altering", back: "Alcohol, drugs, extreme caffeine — all borrow calm from tomorrow." },
      { front: "😴 S — Sleep", back: "Consistent bedtime. Sleep is the biggest emotional regulator you have." },
      { front: "🏃 E — Exercise", back: "Movement, even small. Regularly. Non-negotiable if possible." },
    ],
    practicePrompt: "Which PLEASE letter is most out of balance for you right now? What's one small step this week?",
    scenario: {
      prompt: "You've been snapping at everyone, crying at small things. You're doing therapy, journalling, meditating. Nothing helps. What might be missing?",
      options: [
        { text: "More therapy.", correct: false, feedback: "Maybe — but not first. Check the basics." },
        { text: "PLEASE: are you sleeping, eating, moving, not overdoing caffeine or alcohol?", correct: true, feedback: "Yes. If PLEASE is off, no other skill works. It's the ground everything else stands on." },
        { text: "Push through — everyone has hard weeks.", correct: false, feedback: "Pushing through is what dysregulates you further. Regulation starts with the body." },
        { text: "New medication.", correct: false, feedback: "Sometimes right — but check PLEASE first with a professional." },
      ],
    },
    reflectionPrompt: "Which one PLEASE change would make the biggest difference this month? Start there.",
  },
  {
    id: "positive-experiences",
    title: "Build Positive Experiences",
    emoji: "🌻",
    summary: "Small pleasant things, deliberately added and actually noticed.",
    learn: [
      "You don't build a good life waiting for good things to happen. You build it by deliberately adding small pleasant experiences into your day — and, crucially, letting yourself notice them when they happen.",
      "The 'letting yourself notice' part is what most people skip. A trauma-trained nervous system often braces for what's next, even during good moments. Practise savouring: five seconds longer than usual, five senses if you can.",
      "One tiny thing daily. One larger thing weekly. One meaningful thing monthly. Track it if it helps. This isn't fluff — this is nervous-system evidence that safety exists.",
    ],
    flashcards: [
      { front: "🌻 The rhythm", back: "Tiny daily. Larger weekly. Meaningful monthly." },
      { front: "The hard part", back: "Letting yourself notice it when it's happening. Savour five seconds longer." },
      { front: "Why it matters", back: "Nervous-system evidence that safety and pleasure are real." },
      { front: "Trap", back: "Bracing for what's next during a good moment. Notice the brace. Stay a little longer." },
    ],
    practicePrompt: "What's one small pleasant thing you'll deliberately add tomorrow? What's one you'll let yourself notice fully when it happens?",
    scenario: {
      prompt: "A friend compliments your work. You feel a warm flicker — then immediately: 'They're just being nice.' What's the skill?",
      options: [
        { text: "Deflect: 'oh it was nothing.'", correct: false, feedback: "That's the brace kicking in. Deflecting protects you from the pleasure of being seen." },
        { text: "Pause. Feel the warm flicker for five seconds. Say thank you. Let it in.", correct: true, feedback: "Yes. Building positive experiences includes savouring them when they arrive." },
        { text: "Assume they're right.", correct: false, feedback: "You don't need to overcorrect — just don't rush past it." },
        { text: "Point out something wrong with your work.", correct: false, feedback: "That's the brace and the deflect combined. Let the warmth in for a moment first." },
      ],
    },
    reflectionPrompt: "What's your relationship with pleasure? What happens in your body when something good arrives?",
  },
  {
    id: "cope-ahead",
    title: "Cope Ahead",
    emoji: "🎯",
    summary: "Rehearse the hard thing before you're in it.",
    learn: [
      "Cope Ahead is DBT's version of a mental fire drill. You know a hard situation is coming — the meeting, the party, the family visit. Instead of hoping for the best, you rehearse.",
      "The steps: describe the situation. Decide which skills you'll use. Imagine it clearly — including the hard moment. Rehearse yourself using the skills. Then plan how you'll take care of yourself afterwards.",
      "Rehearsal isn't the same as worrying. Worry loops without a plan. Cope Ahead ends with a specific plan and often a lower baseline anxiety about the event.",
    ],
    flashcards: [
      { front: "🎯 The five steps", back: "Describe · Decide skills · Imagine clearly · Rehearse skills in imagination · Plan self-care after." },
      { front: "Not the same as worry", back: "Worry loops without a plan. Cope Ahead ends with one." },
      { front: "When to use it", back: "Any predictable hard situation — meeting, party, appointment, family." },
      { front: "The bonus", back: "Rehearsal often lowers baseline anxiety about the event, before it even arrives." },
    ],
    practicePrompt: "What's a hard situation this week? Run through the five Cope Ahead steps for it now.",
    scenario: {
      prompt: "You've got a family dinner on Sunday where a difficult relative will be there. What's Cope Ahead?",
      options: [
        { text: "Don't think about it — you'll deal with it on the day.", correct: false, feedback: "That's the opposite of Cope Ahead. Unrehearsed hard moments usually go worse." },
        { text: "Worry about it all week.", correct: false, feedback: "Worrying isn't rehearsing. It's the same loop without a plan." },
        { text: "Picture the room, pick two skills to use (STOP, self-soothe), imagine using them, plan a quiet Sunday evening after.", correct: true, feedback: "Yes. Rehearsed skills, planned aftercare — this is how you meet hard days on purpose." },
        { text: "Decide to cancel.", correct: false, feedback: "Sometimes the right call — but if you're going, Cope Ahead makes it survivable." },
      ],
    },
    reflectionPrompt: "What's coming up that you've been avoiding rehearsing? What might change if you did?",
  },
  {
    id: "problem-solving",
    title: "Problem Solving",
    emoji: "🧩",
    summary: "For when the emotion fits — and it's time to change the situation.",
    learn: [
      "Sometimes an emotion fits the facts and the situation genuinely needs to change. Problem Solving is the DBT skill for those moments — a clear, structured way to move from stuck to acted-on.",
      "The steps: describe the problem specifically. Check the facts. Identify your goal. List every possible solution — even ones you'd reject. Choose one and plan the first step. Do it. Evaluate.",
      "Most of us skip 'list every possible solution' and go straight to the first idea. Listing more options widens what feels possible — and the eventual choice is usually better.",
    ],
    flashcards: [
      { front: "🧩 When to use it", back: "When the emotion fits and the situation actually needs to change." },
      { front: "The steps", back: "Describe · Check facts · Set goal · List all solutions · Choose · Plan first step · Do · Evaluate." },
      { front: "Don't skip", back: "'List every solution.' Even bad ones. Widens what feels possible." },
      { front: "First step", back: "Small enough to actually do this week. Not the whole plan — the first move." },
    ],
    practicePrompt: "Pick one stuck situation. Walk through the steps. What's the first step you can take this week?",
    scenario: {
      prompt: "You're burning out at your job. What's Problem Solving here?",
      options: [
        { text: "Push through until you break.", correct: false, feedback: "That's what happens when Problem Solving is skipped. Emotion fits — situation needs to change." },
        { text: "Quit immediately.", correct: false, feedback: "That's the first idea, not the best one. List more options before choosing." },
        { text: "Describe specifically what's burning you out. List every possible solution — hours, role, team, boss talk, sabbatical, quit. Pick one first step this week.", correct: true, feedback: "Yes. Specific problem, wide options, one first step. That's the skill." },
        { text: "Vent to friends and keep going.", correct: false, feedback: "Venting is coping — not solving. Both matter, but the situation still needs the plan." },
      ],
    },
    reflectionPrompt: "What situation have you been coping with instead of solving? What would the first step look like?",
  },
];

// ─────────────────────────────────────────────────────────────
// 4. INTERPERSONAL EFFECTIVENESS
// ─────────────────────────────────────────────────────────────
const interpersonal: DbtSkill[] = [
  {
    id: "dear-man",
    title: "DEAR MAN",
    emoji: "💪",
    summary: "How to ask for something — clearly, calmly, effectively.",
    learn: [
      "DEAR MAN is DBT's script for the moments you need to ask for something or say no, and it matters that you do it well. It sounds mechanical the first time — and works anyway.",
      "DEAR is the message: Describe (facts), Express (feelings), Assert (the ask), Reinforce (what's in it for them). MAN is the delivery: Mindful (stay on point), Appear confident, Negotiate (be willing to compromise on the how, not the what).",
      "Use it for high-stakes conversations. Rehearse it before you say it. And expect that even a perfect DEAR MAN doesn't guarantee a yes — it just gives you the best chance of being heard.",
    ],
    flashcards: [
      { front: "💬 D — Describe", back: "The facts, no judgement. 'When you cancelled twice in a row…'" },
      { front: "😊 E — Express", back: "Your feelings. 'I felt hurt and unimportant.'" },
      { front: "🎯 A — Assert", back: "The ask, clearly. 'I'd like us to plan a rescheduled date this week.'" },
      { front: "💖 R — Reinforce", back: "What's in it for them. 'It would mean a lot and we'd both actually get to see each other.'" },
      { front: "🧘 M — Mindful", back: "Stay on point. Don't get pulled into old fights or defence." },
      { front: "😐 A — Appear confident", back: "Steady voice. Eye contact. Even when you don't feel it." },
      { front: "🤝 N — Negotiate", back: "Be willing to compromise on the how — not the what. 'If not this week, then when?'" },
    ],
    practicePrompt: "Write one DEAR MAN script for something you've been avoiding asking for. Say it out loud once.",
    scenario: {
      prompt: "Your flatmate keeps leaving dishes in the sink. What's a strong DEAR MAN?",
      options: [
        { text: "'You never clean up — it's driving me insane.'", correct: false, feedback: "That's Express only, wrapped in judgement. No Describe, no Assert, no Reinforce." },
        { text: "'Hey, no worries, whenever you can, sorry to bring it up…'", correct: false, feedback: "Too apologetic. Assert isn't rude — it's clear. You're allowed to ask." },
        { text: "'This week the sink has had dishes in it every night (D). I'm feeling stressed coming home to it (E). Could we agree dishes get done before bed? (A) It'd make the flat feel calmer for both of us (R).'", correct: true, feedback: "Yes. Full DEAR MAN. Clear, kind, hard to argue with." },
        { text: "Send a passive-aggressive photo of the dishes.", correct: false, feedback: "That's the opposite of DEAR MAN. Direct is kinder than passive-aggressive." },
      ],
    },
    reflectionPrompt: "What ask have you been rehearsing in your head for weeks? What would DEAR MAN sound like for it?",
  },
  {
    id: "give",
    title: "GIVE",
    emoji: "🎁",
    summary: "Keeping the relationship intact while you speak up.",
    learn: [
      "GIVE is what DEAR MAN sits inside when the relationship matters long-term. It's the how underneath the what — the difference between being right and being heard.",
      "The four letters: Gentle (no attacks, no threats), Interested (actually listen to their side), Validate (acknowledge their experience, even if you disagree), Easy manner (warmth, humour if it fits — you're not opponents).",
      "GIVE isn't about softening the ask. You can be gentle AND clear. In fact, being gentle usually makes the clarity land better.",
    ],
    flashcards: [
      { front: "🕊️ G — Gentle", back: "No attacks, no threats, no name-calling. Even when you're right." },
      { front: "👂 I — Interested", back: "Actually listen to their side. Don't just wait to talk." },
      { front: "🙏 V — Validate", back: "Acknowledge their experience — 'I can see why you'd see it that way' — even if you disagree." },
      { front: "😊 E — Easy manner", back: "Warmth, humour if it fits. You're not opponents." },
      { front: "Key insight", back: "Gentle ≠ soft on the ask. Gentle makes the ask land better." },
    ],
    practicePrompt: "Bring a difficult person to mind. Which GIVE letter is hardest for you with them? What would it look like to try it once?",
    scenario: {
      prompt: "You're in a hard conversation with your mum and she says something dismissive. What's GIVE?",
      options: [
        { text: "Snap back to defend yourself.", correct: false, feedback: "That's rupture without repair. GIVE keeps the relationship on the table while you speak." },
        { text: "Say nothing and shut down.", correct: false, feedback: "That's not GIVE — that's disappearing. GIVE is gentle AND present." },
        { text: "'I can hear you don't see it the same way (V). I'm not asking you to agree — I just want to explain how it lands for me (G, E).'", correct: true, feedback: "Yes. Validated, gentle, staying warm, still speaking. That's GIVE." },
        { text: "End the conversation.", correct: false, feedback: "Sometimes right — but only after you've tried GIVE. Not as the first move." },
      ],
    },
    reflectionPrompt: "Which relationship needs more GIVE from you? Which letter would you start with?",
  },
  {
    id: "fast",
    title: "FAST",
    emoji: "🛡️",
    summary: "Keeping your self-respect in the conversation.",
    learn: [
      "FAST is the third leg alongside DEAR MAN and GIVE — the one that protects you. It's how you speak up while staying whole.",
      "The four letters: Fair (to yourself and them), no Apologies (for existing, for having needs), Stick to your values, Truthful (no exaggeration, no lying, no scheming).",
      "Trauma survivors often over-apologise as a survival move. FAST gently asks you to notice that pattern and drop it where it's not needed.",
    ],
    flashcards: [
      { front: "⚖️ F — Fair", back: "To yourself and to them. Not one-sided in either direction." },
      { front: "🚫 A — no Apologies", back: "For existing, for having needs, for taking up space. Save apologies for actual harm." },
      { front: "💛 S — Stick to values", back: "Don't abandon what matters to you to make the conversation easier." },
      { front: "💬 T — Truthful", back: "No exaggeration, no lying, no scheming. Truth is the ground you stand on." },
    ],
    practicePrompt: "Which FAST letter is hardest for you? Watch for it this week — where do you slip?",
    scenario: {
      prompt: "You're asking your manager for a change to your schedule. What's FAST?",
      options: [
        { text: "'Sorry to bother you, I know this is a lot to ask, I'll totally understand if it's a no…'", correct: false, feedback: "Way too many apologies. You have a legitimate request. Ask it." },
        { text: "'I need this change or I'll quit.'", correct: false, feedback: "Not fair, and probably not truthful. FAST doesn't mean threatening." },
        { text: "'I'd like to shift my hours to X for Y reason. It matters to me because [value]. Can we talk through what would work?'", correct: true, feedback: "Yes. Fair, no apology for having a need, values-clear, truthful. That's FAST." },
        { text: "Make up a reason that sounds more acceptable.", correct: false, feedback: "That's not Truthful. The real reason is usually enough." },
      ],
    },
    reflectionPrompt: "Where do you over-apologise? What might shift if you dropped the ones that aren't needed?",
  },
  {
    id: "boundaries",
    title: "Setting Boundaries",
    emoji: "🚪",
    summary: "Clear limits, kindly held.",
    learn: [
      "A boundary is a limit you set on your own behaviour, not a rule you impose on someone else. 'I won't stay in conversations where I'm shouted at' is a boundary. 'You must not shout at me' is a rule you can't enforce.",
      "Boundaries protect what you value: your energy, your safety, your time, your integrity. They aren't punishment — they're care for what matters to you.",
      "The three parts: state the limit clearly. Say what you'll do if it's crossed. Follow through. The follow-through is what makes a boundary real — everything else is a hope.",
    ],
    flashcards: [
      { front: "🚪 A boundary is…", back: "A limit you set on your own behaviour, not a rule you impose on someone else." },
      { front: "The three parts", back: "State the limit. Say what you'll do if crossed. Follow through." },
      { front: "The hard part", back: "Follow-through. Without it, a boundary is just a hope." },
      { front: "Not punishment", back: "Boundaries are care for what matters to you — not weapons." },
    ],
    practicePrompt: "Write one boundary in the DBT shape: 'If X happens, I will Y.' Read it back. Can you actually follow through?",
    scenario: {
      prompt: "A family member keeps making cutting comments about your body. What's a good boundary?",
      options: [
        { text: "'You have to stop commenting on my body.'", correct: false, feedback: "That's a rule for them, not a boundary. You can't enforce their behaviour." },
        { text: "'If you comment on my body, I'll change the subject or leave the conversation.' Then do it.", correct: true, feedback: "Yes. Your action, clearly stated, and followed through — that's a boundary." },
        { text: "Say nothing and stew about it.", correct: false, feedback: "That's tolerating what's not okay. Boundaries speak up — kindly, but clearly." },
        { text: "Cut them off entirely without a conversation.", correct: false, feedback: "Sometimes right — but often boundaries can be tried first before going that far." },
      ],
    },
    reflectionPrompt: "Where is a boundary overdue in your life? What would you actually do if it were crossed?",
  },
  {
    id: "validation",
    title: "Validation",
    emoji: "🙏",
    summary: "Acknowledging someone's experience — including your own.",
    learn: [
      "Validation is saying: 'your experience makes sense.' It doesn't mean agreeing. It doesn't mean approving. It means recognising that what someone feels is real to them and has causes.",
      "'You have every right to be angry' is validation. 'I get why that would feel like too much' is validation. It's the single most powerful move in a difficult conversation — because most people are actually just asking to be seen.",
      "Self-validation matters too. When you feel something, the first move isn't 'should I be feeling this?' — it's 'this feeling makes sense; here's what it might be about.'",
    ],
    flashcards: [
      { front: "🙏 What it is", back: "'Your experience makes sense.' Not agreement — recognition." },
      { front: "What it isn't", back: "Approval. Endorsement. Saying they're right. Fixing." },
      { front: "The power move", back: "Most people are asking to be seen, not to be persuaded." },
      { front: "Self-validation", back: "'This feeling makes sense.' Before 'should I be feeling this?'" },
    ],
    practicePrompt: "Practise validating yourself once today when a feeling arrives. Say: 'this makes sense because…'",
    scenario: {
      prompt: "Your friend vents about a hard day at work. What's validation?",
      options: [
        { text: "'You should quit that job.'", correct: false, feedback: "That's advice. Advice before validation usually lands as dismissive." },
        { text: "'It could be worse — at least you have a job.'", correct: false, feedback: "That's invalidation dressed as perspective. It makes people feel worse." },
        { text: "'That sounds exhausting. It makes complete sense you're wiped out.'", correct: true, feedback: "Yes. Recognition. Now they feel seen — and can hear your thoughts if they want them." },
        { text: "'I know exactly how you feel.'", correct: false, feedback: "Often lands as making it about you. Better: 'that sounds really hard' — leave space for them." },
      ],
    },
    reflectionPrompt: "Who's the hardest person for you to validate? Yourself? A specific person? What might shift with one honest 'that makes sense'?",
  },
  {
    id: "assertive-communication",
    title: "Assertive Communication",
    emoji: "🗣️",
    summary: "The middle path between passive and aggressive.",
    learn: [
      "Assertive communication is saying what you think, feel, and need — clearly, honestly, and with respect for the other person. It's the middle path between passive (giving up your position) and aggressive (steamrolling theirs).",
      "The core move: 'I' statements. 'I feel X when Y happens. I'd like Z.' It centres your experience rather than accusing theirs — which usually invites conversation instead of defensiveness.",
      "Assertive doesn't mean loud, forceful, or dominant. It means clear and self-respecting. Quiet assertiveness is often the most powerful kind.",
    ],
    flashcards: [
      { front: "🗣️ The middle path", back: "Between passive (giving up your position) and aggressive (steamrolling theirs)." },
      { front: "The move", back: "'I feel X when Y happens. I'd like Z.'" },
      { front: "Why 'I' statements", back: "Centre your experience. Invite conversation. Reduce defensiveness." },
      { front: "Common myth", back: "Assertive = loud. Actually, quiet assertive is often the strongest." },
    ],
    practicePrompt: "Rewrite one 'you' statement you've been carrying as an 'I' statement. Which one feels different to say?",
    scenario: {
      prompt: "Your partner interrupts you again. What's assertive?",
      options: [
        { text: "Say nothing and feel invisible.", correct: false, feedback: "That's passive. Assertive speaks up, kindly and clearly." },
        { text: "'You always cut me off — you don't listen.'", correct: false, feedback: "That's aggressive with a 'you' statement. Predictable outcome: defence." },
        { text: "'I want to finish what I was saying. Can I have another minute?'", correct: true, feedback: "Yes. Clear, self-respecting, non-attacking. Middle path." },
        { text: "Sigh loudly and leave the room.", correct: false, feedback: "Passive-aggressive. That's neither passive nor assertive — it's both worst options." },
      ],
    },
    reflectionPrompt: "Where do you slip into passive? Where do you slip into aggressive? What's the middle path look like there?",
  },
];

// ─────────────────────────────────────────────────────────────
// 5. WALKING THE MIDDLE PATH
// ─────────────────────────────────────────────────────────────
const middlePath: DbtSkill[] = [
  {
    id: "dialectical-thinking",
    title: "Dialectical Thinking",
    emoji: "☯️",
    summary: "Two opposing truths can both be true.",
    learn: [
      "Dialectical thinking is holding two things at once that seem to contradict — and finding that both are true. 'I love my mum AND she hurt me.' 'I'm doing my best AND I want to do more.' 'This is hard AND I can bear it.'",
      "The trap of black-and-white thinking is that it makes you pick one truth and reject the other — which usually feels wrong because both are real. Dialectics lets you keep both.",
      "The connecting word is 'and,' not 'but.' 'I love my mum but she hurt me' cancels the first half. 'I love my mum AND she hurt me' holds both.",
    ],
    flashcards: [
      { front: "☯️ The move", back: "Hold two things that seem to contradict — and find both are true." },
      { front: "Examples", back: "'I love them AND they hurt me.' 'I'm doing my best AND I want more.' 'This is hard AND I can bear it.'" },
      { front: "The word", back: "AND, not BUT. 'But' cancels the first half. 'And' holds both." },
      { front: "Why it heals", back: "Life is rarely one clean truth. Dialectics lets you meet it as it is." },
    ],
    practicePrompt: "Write one 'and' sentence from your life today: two things that both feel true, even though they seem to contradict.",
    scenario: {
      prompt: "You're grieving a friendship that ended AND you're relieved it's over. What's dialectical?",
      options: [
        { text: "Pick one — you can't be both.", correct: false, feedback: "That's the trap. Both are true. Picking one denies half of what's actually happening in you." },
        { text: "Feel guilty for the relief.", correct: false, feedback: "That's what happens when dialectics is missing. Relief and grief can coexist without contradiction." },
        { text: "Let both be true. Grieve the loss. Honour the relief.", correct: true, feedback: "Yes. Both real, both allowed. That's dialectical thinking." },
        { text: "Focus only on the relief so you don't have to grieve.", correct: false, feedback: "That's suppressing one truth. Dialectics keeps both." },
      ],
    },
    reflectionPrompt: "What two truths are both true for you right now that you've been trying to pick between?",
  },
  {
    id: "validation-middle",
    title: "Validation (both sides)",
    emoji: "🌗",
    summary: "Validate them AND yourself — you don't have to pick.",
    learn: [
      "In conflict, most people think they have to prove they're right — which means proving the other person is wrong. Middle path validation says: both experiences can be valid.",
      "You can say 'I see why you experienced it that way' AND 'here's how it landed for me.' You don't have to invalidate them to honour your own experience.",
      "This is especially powerful in family and long-term relationships, where 'who was right' matters far less than 'do we both feel seen.'",
    ],
    flashcards: [
      { front: "🌗 Both sides", back: "You can validate them AND yourself. Neither cancels the other." },
      { front: "The move", back: "'I see why you saw it that way. AND here's how it landed for me.'" },
      { front: "Why it matters", back: "In lasting relationships, 'both feel seen' matters more than 'who was right.'" },
      { front: "Trap to avoid", back: "Validating them by invalidating yourself. Or vice versa. Hold both." },
    ],
    practicePrompt: "Think of a recent conflict. Write one sentence validating them AND one validating yourself. Both true.",
    scenario: {
      prompt: "You and your partner disagree about a family decision. Both of you feel unheard. What's the middle path?",
      options: [
        { text: "Prove you're right with more evidence.", correct: false, feedback: "Being right doesn't make either of you feel heard. It usually digs the trench deeper." },
        { text: "Give in to keep the peace.", correct: false, feedback: "That's self-invalidation. Middle path validates both — including you." },
        { text: "'I can see why this matters so much to you (V them). It matters to me for different reasons — can I share them? (V self)'", correct: true, feedback: "Yes. Both experiences on the table, neither erased. That's middle-path validation." },
        { text: "Table it forever.", correct: false, feedback: "Avoidance isn't middle path. Middle path is holding both — not skipping both." },
      ],
    },
    reflectionPrompt: "In what conflict have you invalidated yourself to keep the peace? What might change if you held both?",
  },
  {
    id: "flexible-thinking",
    title: "Flexible Thinking",
    emoji: "🌿",
    summary: "Loosen 'always/never' — reality has more shades.",
    learn: [
      "Rigid thinking uses absolute words: always, never, everyone, no one, must, should. Flexible thinking notices these and loosens them — because reality is almost never absolute.",
      "'I always mess things up' becomes 'I messed this up. And I've done things well.' 'No one cares about me' becomes 'I feel unseen right now. Some people have shown they care.'",
      "This isn't positive thinking. It's accurate thinking. Absolutes are almost always inaccurate — and inaccuracy makes emotions worse than they need to be.",
    ],
    flashcards: [
      { front: "🌿 The words to watch", back: "Always, never, everyone, no one, must, should." },
      { front: "The move", back: "Loosen the absolute. 'Always' → 'often, and not always.' 'No one' → 'not who I hoped, and some do.'" },
      { front: "Not positive thinking", back: "It's accurate thinking. Absolutes are almost always inaccurate." },
      { front: "Why emotions ease", back: "Inaccurate stories make emotions worse than they need to be. Accuracy softens." },
    ],
    practicePrompt: "Catch one 'always/never' thought today. Rewrite it with more accuracy. Notice what shifts.",
    scenario: {
      prompt: "You keep thinking 'nothing ever works out for me.' What's flexible thinking?",
      options: [
        { text: "'Everything works out perfectly!'", correct: false, feedback: "That's positive thinking, and it's usually as inaccurate as the negative. Flexible is accurate." },
        { text: "Argue with the thought.", correct: false, feedback: "Arguing usually strengthens it. Notice, then loosen." },
        { text: "'Some things haven't worked out — this one especially. And some things have. Both are true.'", correct: true, feedback: "Yes. Loosened, accurate, dialectical. That's flexible thinking." },
        { text: "Believe the thought completely.", correct: false, feedback: "Rigid absolutes make emotions bigger than they need to be. Loosen." },
      ],
    },
    reflectionPrompt: "What's one 'always' or 'never' thought you're carrying? What's the more accurate version?",
  },
  {
    id: "behaviour-change",
    title: "Behaviour Change",
    emoji: "🌱",
    summary: "Small, honest change — for yourself or others.",
    learn: [
      "Middle path behaviour change balances acceptance and change: 'I accept who I am AND I want to keep growing.' Neither self-flagellation nor complacency.",
      "For yourself: pick one small, specific change. Notice progress kindly. When you slip, no shame — just the return to the change. Behaviour change is a series of returns, not one leap.",
      "For others: reinforce what you want to see, gently disengage from what you don't. Punishment and lecturing rarely change behaviour. Consistent small reinforcement often does.",
    ],
    flashcards: [
      { front: "🌱 Middle path", back: "'I accept who I am AND I want to keep growing.' Both true." },
      { front: "For yourself", back: "One small change. Kind noticing. Return without shame when you slip." },
      { front: "For others", back: "Reinforce what you want. Gently disengage from what you don't. Not lecture." },
      { front: "The pattern", back: "Change is a series of returns, not one leap." },
    ],
    practicePrompt: "What's one small behaviour you want to change? What's the tiniest version of it you could return to daily?",
    scenario: {
      prompt: "You wanted to journal every morning. You've missed four days. What's middle-path behaviour change?",
      options: [
        { text: "Shame yourself for failing.", correct: false, feedback: "Shame doesn't create change — it usually creates more avoidance." },
        { text: "Give up on the habit.", correct: false, feedback: "That's the flip side of shame. Both are all-or-nothing." },
        { text: "Notice kindly. Journal today, even one line. Return.", correct: true, feedback: "Yes. Kind noticing, small return, no drama. That's how habits actually build." },
        { text: "Overhaul the routine to catch up.", correct: false, feedback: "Overcorrection often leads to another crash. Small, consistent return is more durable." },
      ],
    },
    reflectionPrompt: "Which change have you been trying to make with shame? What would kindness look like instead?",
  },
  {
    id: "finding-balance",
    title: "Finding Balance",
    emoji: "⚖️",
    summary: "Between acceptance and change, self and others, effort and rest.",
    learn: [
      "The whole point of walking the middle path is that life keeps swinging between opposites, and skill is knowing which one is needed now.",
      "Some days need acceptance — sitting with what is. Some need change — moving what can move. Some need effort — pushing forward. Some need rest — dropping the push. Most weeks need all four in some measure.",
      "Balance isn't a fixed point. It's the ongoing practice of noticing what this moment needs and offering it — without demanding that today look like yesterday.",
    ],
    flashcards: [
      { front: "⚖️ The pairs", back: "Acceptance ↔ Change · Self ↔ Others · Effort ↔ Rest · Logic ↔ Emotion." },
      { front: "The skill", back: "Knowing which side of the pair this moment needs." },
      { front: "Balance isn't", back: "A fixed point. It's ongoing noticing and adjusting." },
      { front: "The move", back: "Ask: what does this moment need? Offer that — without demanding today look like yesterday." },
    ],
    practicePrompt: "Look at today. Which pair is most out of balance? What would tipping it back one degree look like?",
    scenario: {
      prompt: "You've been pushing hard for weeks and you're exhausted, but there's more to do. What does middle path say?",
      options: [
        { text: "Push through — that's what you always do.", correct: false, feedback: "That's ignoring the pair. Effort with no rest breaks eventually." },
        { text: "Give up entirely.", correct: false, feedback: "That's the flip. All rest without effort has its own cost." },
        { text: "Rest today. Return to effort tomorrow with a clearer head.", correct: true, feedback: "Yes. That's balance — not permanent rest or permanent push. Both, as needed." },
        { text: "Guilt-rest — take a break but hate yourself for it.", correct: false, feedback: "That's rest without acceptance. The rest doesn't restore you if you're beating yourself up." },
      ],
    },
    reflectionPrompt: "Which pair (acceptance/change, self/others, effort/rest, logic/emotion) is most out of balance for you right now?",
  },
];

// ─────────────────────────────────────────────────────────────
// MODULE EXPORTS
// ─────────────────────────────────────────────────────────────

export const DBT_MODULES: DbtModule[] = [
  {
    id: "mindfulness",
    label: "Be Mindful",
    emoji: "🧘",
    tagline: "Notice the present without judgement",
    intro: "The foundation of every DBT skill — learning to slow down, observe your thoughts, and reconnect with the present moment.",
    theme: "sage",
    skills: mindfulness,
  },
  {
    id: "distress-tolerance",
    label: "Distress Tolerance",
    emoji: "🌊",
    tagline: "Get through overwhelming moments without making them worse",
    intro: "Practical skills for surviving emotional crises safely — riding the wave until it passes.",
    theme: "sky",
    skills: distressTolerance,
  },
  {
    id: "emotion-regulation",
    label: "Emotion Regulation",
    emoji: "❤️",
    tagline: "Understand your emotions instead of being controlled by them",
    intro: "Skills for meeting your emotions as messengers, not enemies — and reducing the intensity when they overwhelm.",
    theme: "peach",
    skills: emotionRegulation,
  },
  {
    id: "interpersonal",
    label: "Interpersonal Effectiveness",
    emoji: "🤝",
    tagline: "Build healthier relationships while respecting yourself",
    intro: "Skills for asking for what you need, saying no, and keeping relationships healthy — including with yourself.",
    theme: "lavender",
    skills: interpersonal,
  },
  {
    id: "middle-path",
    label: "Walking the Middle Path",
    emoji: "🌱",
    tagline: "Balance acceptance with change",
    intro: "Holding two truths at once — accepting who you are AND continuing to grow.",
    theme: "gold",
    skills: middlePath,
  },
];

// Helpers
export function findModule(id: string): DbtModule | undefined {
  return DBT_MODULES.find((m) => m.id === id);
}

export function findSkill(
  moduleId: string,
  skillId: string,
): { module: DbtModule; skill: DbtSkill; index: number } | undefined {
  const module = findModule(moduleId);
  if (!module) return undefined;
  const index = module.skills.findIndex((s) => s.id === skillId);
  if (index < 0) return undefined;
  return { module, skill: module.skills[index], index };
}

/** Returns the next skill in the same module, or the first skill of the next module. */
export function nextSkill(
  moduleId: string,
  skillId: string,
): { moduleId: DbtModuleId; skillId: string } | undefined {
  const modIdx = DBT_MODULES.findIndex((m) => m.id === moduleId);
  if (modIdx < 0) return undefined;
  const module = DBT_MODULES[modIdx];
  const skillIdx = module.skills.findIndex((s) => s.id === skillId);
  if (skillIdx < 0) return undefined;
  if (skillIdx + 1 < module.skills.length) {
    return { moduleId: module.id, skillId: module.skills[skillIdx + 1].id };
  }
  const nextMod = DBT_MODULES[modIdx + 1];
  if (nextMod) {
    return { moduleId: nextMod.id, skillId: nextMod.skills[0].id };
  }
  return undefined;
}

export function totalSkillCount(): number {
  return DBT_MODULES.reduce((n, m) => n + m.skills.length, 0);
}
